import { supabase } from '../lib/supabase';

/**
 * Service pour gérer les notifications et rappels de paiement
 * Interagit avec les tables Supabase créées pour le système de notifications
 */
class PaymentNotificationService {
  
  // ==========================================
  // GESTION DES PARAMÈTRES DE NOTIFICATION
  // ==========================================

  /**
   * Récupère les paramètres de notification d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object|null>} - Paramètres de notification ou null
   */
  async getNotificationSettings(userId) {
    try {
      const { data, error } = await supabase
        .from('payment_notification_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Aucun paramètre trouvé, retourner les valeurs par défaut
          return this.getDefaultSettings();
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres:', error);
      throw error;
    }
  }

  /**
   * Retourne les paramètres par défaut
   * @returns {Object} - Paramètres par défaut
   */
  getDefaultSettings() {
    return {
      enableReminders: true,
      reminderDaysBefore: 7,
      enableOverdueAlerts: true,
      overdueCheckFrequency: 'daily',
      escalationDays: 3,
      notificationChannels: {
        email: true,
        dashboard: true,
        sms: false
      },
      reminderTemplate: 'Rappel : Votre paiement de {amount} pour la facture {invoice_number} est dû le {due_date}.',
      overdueTemplate: 'Paiement en retard : La facture {invoice_number} d\'un montant de {amount} est en retard de {days_overdue} jours.'
    };
  }

  /**
   * Sauvegarde ou met à jour les paramètres de notification d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} settings - Paramètres à sauvegarder
   * @returns {Promise<Object>} - Paramètres sauvegardés
   */
  async saveNotificationSettings(userId, settings) {
    try {
      const { data, error } = await supabase
        .from('payment_notification_settings')
        .upsert({
          user_id: userId,
          enable_reminders: settings.enableReminders,
          reminder_days_before: settings.reminderDaysBefore,
          reminder_template: settings.reminderTemplate,
          enable_overdue_alerts: settings.enableOverdueAlerts,
          overdue_check_frequency: settings.overdueCheckFrequency,
          escalation_days: settings.escalationDays,
          overdue_template: settings.overdueTemplate,
          notification_channels: settings.notificationChannels,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      throw error;
    }
  }

  // ==========================================
  // GESTION DES RAPPELS DE PAIEMENT
  // ==========================================

  /**
   * Crée un rappel de paiement pour une facture
   * @param {Object} reminderData - Données du rappel
   * @returns {Promise<Object>} - Rappel créé
   */
  async createPaymentReminder(reminderData) {
    try {
      const { data, error } = await supabase
        .from('payment_reminders')
        .insert({
          user_id: reminderData.userId,
          invoice_id: reminderData.invoiceId,
          case_phase_invoice_id: reminderData.casePhaseInvoiceId,
          reminder_type: reminderData.reminderType,
          scheduled_date: reminderData.scheduledDate,
          amount: reminderData.amount,
          invoice_number: reminderData.invoiceNumber,
          due_date: reminderData.dueDate,
          days_overdue: reminderData.daysOverdue || 0,
          message_text: reminderData.messageText,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors de la création du rappel:', error);
      throw error;
    }
  }

  /**
   * Crée automatiquement des rappels pour une facture selon les paramètres utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} invoice - Données de la facture
   * @returns {Promise<Array>} - Rappels créés
   */
  async createRemindersForInvoice(userId, invoice) {
    try {
      const settings = await this.getNotificationSettings(userId);
      const reminders = [];

      if (!invoice.due_date) {
        console.warn('Facture sans date d\'échéance, impossible de créer des rappels');
        return reminders;
      }

      const dueDate = new Date(invoice.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Rappel avant échéance
      if (settings.enableReminders) {
        const reminderDate = new Date(dueDate);
        reminderDate.setDate(reminderDate.getDate() - settings.reminderDaysBefore);

        if (reminderDate >= today) {
          const reminderData = {
            userId: userId,
            invoiceId: invoice.id,
            reminderType: 'before_due',
            scheduledDate: reminderDate.toISOString().split('T')[0],
            amount: invoice.total_amount,
            invoiceNumber: invoice.invoice_number,
            dueDate: invoice.due_date,
            messageText: this.formatMessage(settings.reminderTemplate, {
              amount: this.formatCurrency(invoice.total_amount),
              invoice_number: invoice.invoice_number,
              due_date: this.formatDate(invoice.due_date),
              client_name: invoice.client_name || ''
            })
          };

          const reminder = await this.createPaymentReminder(reminderData);
          reminders.push(reminder);
        }
      }

      return reminders;
    } catch (error) {
      console.error('Erreur lors de la création automatique des rappels:', error);
      throw error;
    }
  }

  /**
   * Récupère tous les rappels d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<Array>} - Liste des rappels
   */
  async getUserReminders(userId, filters = {}) {
    try {
      let query = supabase
        .from('payment_reminders')
        .select(`
          *,
          invoice:invoices(invoice_number, client_name, total_amount, status)
        `)
        .eq('user_id', userId)
        .order('scheduled_date', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.reminderType) {
        query = query.eq('reminder_type', filters.reminderType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des rappels:', error);
      throw error;
    }
  }

  /**
   * Marque un rappel comme envoyé
   * @param {string} reminderId - ID du rappel
   * @param {string} channel - Canal utilisé (email, sms, dashboard)
   * @returns {Promise<Object>} - Rappel mis à jour
   */
  async markReminderAsSent(reminderId, channel) {
    try {
      // Utiliser la fonction SQL personnalisée
      const { error } = await supabase.rpc('mark_reminder_as_sent', {
        reminder_id: reminderId,
        channel_name: channel
      });

      if (error) throw error;

      // Récupérer le rappel mis à jour
      const { data } = await supabase
        .from('payment_reminders')
        .select('*')
        .eq('id', reminderId)
        .single();

      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rappel:', error);
      throw error;
    }
  }

  /**
   * Annule un rappel
   * @param {string} reminderId - ID du rappel
   * @returns {Promise<Object>} - Rappel annulé
   */
  async cancelReminder(reminderId) {
    try {
      const { data, error } = await supabase
        .from('payment_reminders')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', reminderId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors de l\'annulation du rappel:', error);
      throw error;
    }
  }

  // ==========================================
  // GESTION DES NOTIFICATIONS DASHBOARD
  // ==========================================

  /**
   * Crée une notification dans le dashboard
   * @param {Object} notificationData - Données de la notification
   * @returns {Promise<Object>} - Notification créée
   */
  async createDashboardNotification(notificationData) {
    try {
      const { data, error } = await supabase
        .from('dashboard_notifications')
        .insert({
          user_id: notificationData.userId,
          title: notificationData.title,
          message: notificationData.message,
          type: notificationData.type || 'info',
          action_url: notificationData.actionUrl,
          action_label: notificationData.actionLabel,
          invoice_id: notificationData.invoiceId,
          case_id: notificationData.caseId,
          priority: notificationData.priority || 0,
          expires_at: notificationData.expiresAt
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error);
      throw error;
    }
  }

  /**
   * Récupère les notifications non lues d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Array>} - Notifications non lues
   */
  async getUnreadNotifications(userId) {
    try {
      const { data, error } = await supabase
        .from('dashboard_notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('is_read', false)
        .eq('is_dismissed', false)
        .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
      throw error;
    }
  }

  /**
   * Marque une notification comme lue
   * @param {string} notificationId - ID de la notification
   * @returns {Promise<Object>} - Notification mise à jour
   */
  async markNotificationAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        .from('dashboard_notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la notification:', error);
      throw error;
    }
  }

  /**
   * Rejette/masque une notification
   * @param {string} notificationId - ID de la notification
   * @returns {Promise<Object>} - Notification mise à jour
   */
  async dismissNotification(notificationId) {
    try {
      const { data, error } = await supabase
        .from('dashboard_notifications')
        .update({
          is_dismissed: true,
          dismissed_at: new Date().toISOString()
        })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors du masquage de la notification:', error);
      throw error;
    }
  }

  // ==========================================
  // HISTORIQUE DES NOTIFICATIONS
  // ==========================================

  /**
   * Ajoute une entrée dans l'historique des notifications
   * @param {Object} historyData - Données de l'historique
   * @returns {Promise<Object>} - Entrée d'historique créée
   */
  async addNotificationHistory(historyData) {
    try {
      const { data, error } = await supabase
        .from('notification_history')
        .insert({
          user_id: historyData.userId,
          reminder_id: historyData.reminderId,
          invoice_id: historyData.invoiceId,
          notification_type: historyData.notificationType,
          channel: historyData.channel,
          subject: historyData.subject,
          message: historyData.message,
          status: historyData.status || 'sent',
          error_message: historyData.errorMessage,
          metadata: historyData.metadata || {}
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erreur lors de l\'ajout à l\'historique:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique des notifications d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} options - Options de filtrage
   * @returns {Promise<Array>} - Historique des notifications
   */
  async getNotificationHistory(userId, options = {}) {
    try {
      let query = supabase
        .from('notification_history')
        .select('*')
        .eq('user_id', userId)
        .order('sent_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.channel) {
        query = query.eq('channel', options.channel);
      }

      if (options.notificationType) {
        query = query.eq('notification_type', options.notificationType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      throw error;
    }
  }

  // ==========================================
  // VÉRIFICATION ET TRAITEMENT DES RAPPELS
  // ==========================================

  /**
   * Vérifie et traite les factures en retard
   * Crée des rappels de retard si nécessaire
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Array>} - Rappels créés
   */
  async checkOverdueInvoices(userId) {
    try {
      const settings = await this.getNotificationSettings(userId);

      if (!settings.enableOverdueAlerts) {
        return [];
      }

      // Récupérer toutes les factures en retard
      const { data: overdueInvoices, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('client_id', userId)
        .lt('due_date', new Date().toISOString().split('T')[0])
        .neq('status', 'paid')
        .neq('status', 'cancelled');

      if (error) throw error;

      const reminders = [];

      for (const invoice of overdueInvoices || []) {
        // Vérifier si un rappel de retard existe déjà pour cette facture
        const { data: existingReminder } = await supabase
          .from('payment_reminders')
          .select('id')
          .eq('invoice_id', invoice.id)
          .eq('reminder_type', 'overdue')
          .eq('status', 'pending')
          .single();

        if (!existingReminder) {
          const daysOverdue = Math.floor(
            (new Date() - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24)
          );

          const reminderData = {
            userId: userId,
            invoiceId: invoice.id,
            reminderType: 'overdue',
            scheduledDate: new Date().toISOString().split('T')[0],
            amount: invoice.total_amount,
            invoiceNumber: invoice.invoice_number,
            dueDate: invoice.due_date,
            daysOverdue: daysOverdue,
            messageText: this.formatMessage(settings.overdueTemplate, {
              amount: this.formatCurrency(invoice.total_amount),
              invoice_number: invoice.invoice_number,
              days_overdue: daysOverdue,
              client_name: invoice.client_name || ''
            })
          };

          const reminder = await this.createPaymentReminder(reminderData);
          reminders.push(reminder);

          // Créer aussi une notification dashboard
          await this.createDashboardNotification({
            userId: userId,
            title: 'Facture en retard',
            message: `La facture ${invoice.invoice_number} est en retard de ${daysOverdue} jours`,
            type: 'warning',
            invoiceId: invoice.id,
            actionUrl: `/billing-overview?invoice=${invoice.id}`,
            actionLabel: 'Voir la facture',
            priority: 8
          });
        }
      }

      return reminders;
    } catch (error) {
      console.error('Erreur lors de la vérification des factures en retard:', error);
      throw error;
    }
  }

  // ==========================================
  // UTILITAIRES
  // ==========================================

  /**
   * Formate un message avec des variables
   * @param {string} template - Template du message
   * @param {Object} variables - Variables à remplacer
   * @returns {string} - Message formaté
   */
  formatMessage(template, variables) {
    let message = template;
    for (const [key, value] of Object.entries(variables)) {
      message = message.replace(new RegExp(`{${key}}`, 'g'), value);
    }
    return message;
  }

  /**
   * Formate un montant en devise
   * @param {number} amount - Montant
   * @param {string} currency - Code devise
   * @returns {string} - Montant formaté
   */
  formatCurrency(amount, currency = 'EUR') {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency
      }).format(amount || 0);
    } catch {
      return `${amount || 0} ${currency}`;
    }
  }

  /**
   * Formate une date
   * @param {string|Date} date - Date à formater
   * @returns {string} - Date formatée
   */
  formatDate(date) {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}

export default new PaymentNotificationService();